"use client";

import React from "react";
import { ShoppingCart, Trash2, Plus, Minus } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "nextjs-toploader/app";
import { Sheet, SheetContent, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton"; // Import Skeleton
import { useCurrentLocale, useScopedI18n } from "@/locales/client";
import { formatCurrency } from "@/lib/vendure/formatCurrencty";
import { useOrder } from "@/hooks/mutations/useOrder";
import { useCartDrawer } from "@/hooks/useCartDrawer";
import { useActiveOrderQuery } from "@/hooks/queries/useActiveOrderQuery";
export const CartDrawer = () => {
  const {
    isOpen,
    closeCartDrawer,
    toggleCartDrawer
  } = useCartDrawer();
  const t = useScopedI18n("cart");
  const {
    data: activeOrder,
    isLoading,
    isFetching
  } = useActiveOrderQuery();
  const locale = useCurrentLocale();
  const isAddingItems = activeOrder?.state === "AddingItems";
  const {
    removeItemFromOrder,
    adjustItemQuantity
  } = useOrder();
  const router = useRouter();
  const handleRemoveItem = (orderLineId: string) => {
    if (isAddingItems) {
      removeItemFromOrder.mutate({
        orderLineId
      });
    }
  };
  const handleAdjustQuantity = (orderLineId: string, quantity: number) => {
    if (isAddingItems) {
      adjustItemQuantity.mutate({
        orderLineId,
        quantity
      });
    }
  };
  const handleCheckout = () => {
    closeCartDrawer();
    router.refresh();
    router.push("/checkout");
  };
  return <Sheet open={isOpen} onOpenChange={toggleCartDrawer} data-sentry-element="Sheet" data-sentry-component="CartDrawer" data-sentry-source-file="CartDrawer.tsx">
      <SheetTrigger asChild data-sentry-element="SheetTrigger" data-sentry-source-file="CartDrawer.tsx">
        <Button className="relative" size="icon" variant="ghost" data-sentry-element="Button" data-sentry-source-file="CartDrawer.tsx">
          <ShoppingCart className="h-6 w-6" data-sentry-element="ShoppingCart" data-sentry-source-file="CartDrawer.tsx" />
          <span className="sr-only">{t("openCart")}</span>
          {activeOrder && activeOrder.totalQuantity > 0 && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {activeOrder.totalQuantity}
            </span>}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col md:max-w-[500px]" data-sentry-element="SheetContent" data-sentry-source-file="CartDrawer.tsx">
        <SheetTitle className="text-xl text-primary" data-sentry-element="SheetTitle" data-sentry-source-file="CartDrawer.tsx">
          {t("yourCart")}
        </SheetTitle>
        {isLoading || isFetching ? <div className="mt-4 flex grow flex-col">
            {Array.from({
          length: 3
        }).map((_, index) => <div key={index} className="flex items-center border-b py-4">
                <div className="relative mr-4 h-20 w-20 shrink-0">
                  <Skeleton className="h-full w-full" />{" "}
                  {/* Skeleton for image */}
                </div>
                <div className="flex-grow">
                  <Skeleton className="mb-2 h-4 w-3/4" />{" "}
                  {/* Skeleton for product name */}
                  <Skeleton className="h-4 w-1/2" /> {/* Skeleton for price */}
                  <div className="mt-2 flex items-center">
                    <Skeleton className="mr-2 h-8 w-8" />{" "}
                    {/* Skeleton for minus button */}
                    <Skeleton className="mr-2 h-8 w-8" />{" "}
                    {/* Skeleton for quantity */}
                    <Skeleton className="h-8 w-8" />{" "}
                    {/* Skeleton for plus button */}
                  </div>
                </div>
                <Skeleton className="h-8 w-8" />{" "}
                {/* Skeleton for remove button */}
              </div>)}
          </div> : activeOrder && activeOrder.lines.length > 0 ? <div className="mt-4 flex grow flex-col">
            <div className="flex-grow overflow-auto">
              {activeOrder.lines.map(line => <div key={line.id} className="flex items-center border-b py-4">
                  <div className="relative mr-4 h-20 w-20 shrink-0">
                    <Image fill alt={line.productVariant.name} className="object-cover" src={line.featuredAsset?.preview || ""} />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-medium">{line.productVariant.name}</h3>
                    <p className="text-sm text-slate-500">
                      {formatCurrency(line.linePriceWithTax as number, activeOrder.currencyCode, locale)}
                    </p>
                    <Button disabled={!isAddingItems} size="icon" variant="outline" onClick={() => handleAdjustQuantity(line.id, line.quantity - 1)}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="mx-2">{line.quantity}</span>
                    <Button disabled={!isAddingItems} size="icon" variant="outline" onClick={() => handleAdjustQuantity(line.id, line.quantity + 1)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button disabled={!isAddingItems} size="icon" variant="ghost" onClick={() => handleRemoveItem(line.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>)}
            </div>
            <SheetFooter className="mt-auto">
              <div className="w-full">
                <div className="mb-4 flex justify-between">
                  <span>{t("subtotal")}</span>
                  <span className="font-bold">
                    {formatCurrency(activeOrder.subTotalWithTax as number, activeOrder.currencyCode, locale)}
                  </span>
                </div>
                <Button className="w-full" onClick={handleCheckout}>
                  <span>{t("proceedToCheckout")}</span>
                </Button>
              </div>
            </SheetFooter>
          </div> : <div className="mt-4 flex grow flex-col justify-center text-center">
            <p className="mb-4 text-2xl font-semibold">{t("emptyTitle")}</p>
            <Button asChild className="mt-10">
              <Link href="/shop" onClick={closeCartDrawer}>
                {t("goShopping")}
              </Link>
            </Button>
          </div>}
      </SheetContent>
    </Sheet>;
};